import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { COUNTRIES, DEFAULT_COUNTRY_CODE } from '../../../../../../../config/countries.config';
import {
  StoreLocation,
  StoreLocationInput,
  StoreLocationService,
} from '../../../../../../../services/store-location.service';

/**
 * Adds a location (location = null) or edits one. Saves itself and hands the stored
 * location back, so the list only has to put it in place.
 */
@Component({
  selector: 'app-store-location-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmDialogImports,
    HlmFieldImports,
    HlmInputImports,
    HlmNativeSelectImports,
    HlmSpinnerImports,
  ],
  providers: [provideIcons({ lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './store-location-dialog.component.html',
})
export class StoreLocationDialogComponent {
  private readonly service = inject(StoreLocationService);

  readonly open = input(false);
  /** The location being edited; null adds a new one. */
  readonly location = input<StoreLocation | null>(null);
  readonly saved = output<StoreLocation>();
  readonly close = output<void>();

  protected readonly countries = COUNTRIES;
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly form = inject(FormBuilder).nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    street: ['', [Validators.required, Validators.maxLength(200)]],
    zip: ['', [Validators.required, Validators.maxLength(20)]],
    city: ['', [Validators.required, Validators.maxLength(100)]],
    country: [DEFAULT_COUNTRY_CODE],
  });

  constructor() {
    // Every opening starts from the location (or empty), never from a cancelled edit.
    effect(() => {
      if (!this.open()) return;
      const loc = this.location();
      this.form.reset({
        name: loc?.name ?? '',
        street: loc?.street ?? '',
        zip: loc?.zip ?? '',
        city: loc?.city ?? '',
        country: loc?.country ?? DEFAULT_COUNTRY_CODE,
      });
      this.error.set(null);
    });
  }

  protected isInvalid(field: keyof StoreLocationInput): boolean {
    const control = this.form.controls[field];
    return control.invalid && (control.touched || control.dirty);
  }

  protected onDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && this.open()) this.close.emit();
  }

  protected submit(): void {
    if (this.saving()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const input = this.form.getRawValue();
    const existing = this.location();
    this.saving.set(true);
    this.error.set(null);
    (existing ? this.service.update(existing.id, input) : this.service.create(input)).subscribe({
      next: (location) => {
        this.saving.set(false);
        this.saved.emit(location);
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(
          typeof err.error?.detail === 'string'
            ? err.error.detail
            : 'Der Standort konnte nicht gespeichert werden. Bitte versuche es erneut.',
        );
      },
    });
  }
}
